import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';

/**
 * The amber-border, "!"-popover treatment for a record Roger holds that the
 * reader should look at again before relying on it (EMR-1369).
 *
 * Two surfaces need to say the same thing about different rows. A clinician
 * name imported from Kinnser points at somebody with no Roger account; an
 * imported medication or allergy line is a sentence another EMR held that no
 * clinician has ruled on. Both are *true* — the data is real and showing it is
 * better than hiding it — and both are *not yet ours*. That is one idea, so it
 * gets one treatment, and the treatment lives here rather than being copied.
 *
 * Add Episode uses the same treatment for a third case (EMR-1728): the care
 * team it carries forward, whether that person is an unclaimed import or
 * somebody the picker can no longer offer. The shared part is the claim the
 * border makes — look again — not the reason behind it, which each call site
 * supplies as `lines`.
 *
 * The tone is deliberate: amber, not red. This is not an error state and the
 * reader has not done anything wrong. The border says "look again before you
 * rely on this"; the popover says what would make it ordinary.
 *
 * The wrapper is an inline span so it can sit inside a table cell beside other
 * text without changing the row's layout.
 */
export function UnclaimedRecordMarker({
  children,
  popoverLabel,
  lines,
  className,
  testId = 'unclaimed-record',
}: {
  /** What is being marked — usually a name or a line's display text. */
  children: React.ReactNode;
  /** Accessible name for the "!" button; says what the popover explains. */
  popoverLabel: string;
  /** The explanation, one claim per line, in the chart register. */
  lines: string[];
  /**
   * Layout-only classes for the call site. A name must not wrap
   * (`whitespace-nowrap`); a medication order line is a sentence and should.
   * Colors and the border are not the caller's to change — that is the point
   * of sharing this.
   */
  className?: string;
  /**
   * Base for this instance's `data-testid`s. The badge and popover derive
   * `-badge` and `-popover` from it, so a surface with two marked things can
   * address each one.
   */
  testId?: string;
}) {
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-800${
        className ? ` ${className}` : ''
      }`}
      data-testid={testId}
    >
      {children}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type='button'
            aria-label={popoverLabel}
            data-testid={`${testId}-badge`}
            className='inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold leading-none text-white transition-colors hover:bg-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1'
          >
            !
          </button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          data-testid={`${testId}-popover`}
          className='w-72 max-w-[calc(100vw-2rem)] space-y-1.5 p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-600'
        >
          {lines.map(line => (
            <p key={line}>{line}</p>
          ))}
        </PopoverContent>
      </Popover>
    </span>
  );
}

export default UnclaimedRecordMarker;
